/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { Row } from "../layout/flexbox";
import { IdListSearchDropdown } from "./IdListSearchDropdown";
import { TextSearchDropdown } from "./TextSearchDropdown";
import * as Buttons from "../forms/elements/buttons";
import { ItemKey } from "./multiSelect/types";
import { MultiSelectFilter } from "./multiSelect/MultiSelectFilter";
import { getResource } from "../language/resources/getResource";

type Props = {
  resources: Record<string, any>;

  sellingSeasons: any[];
  fulfillmentSeasons: any[];
  accessNodes: any[];

  selectedSellingSeason: ItemKey[];
  selectedFulfillmentSeason: ItemKey[];
  selectedAccessNodes: ItemKey[];
  entitlementTypeIdQuery: string;
  entitlementTypeDescriptionQuery: string;

  onSellingSeasonChange: (v: ItemKey[]) => void;
  onFulfillmentSeasonChange: (v: ItemKey[]) => void;
  onAccessNodesChange: (v: ItemKey[]) => void;
  onIdQueryChange: (v: string) => void;
  onDescriptionQueryChange: (v: string) => void;
  onValidIdListChange: (ids: number[]) => void;

  onSearch: () => void;
};

const filterControlCss = css`
  display: flex;
  align-items: center;
  height: 50px; /* matches MultiSelectFilter height */
  padding: 0;
  margin: 0;
`;

const searchButtonCss = css`
  height: 50px;
  padding: 0 20px; /* Adjust horizontally as needed */
  margin-right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

export const AccessOverviewFilters = ({
  resources,
  sellingSeasons,
  fulfillmentSeasons,
  accessNodes,

  selectedSellingSeason,
  selectedFulfillmentSeason,
  selectedAccessNodes,
  entitlementTypeIdQuery,
  entitlementTypeDescriptionQuery,

  onSellingSeasonChange,
  onFulfillmentSeasonChange,
  onAccessNodesChange,
  onIdQueryChange,
  onDescriptionQueryChange,
  onValidIdListChange,

  onSearch,
}: Props) => {
  return (
    <Row
      rootCss={css`
        gap: 20px;
        flex-wrap: nowrap;
        align-items: center;
      `}
    >
      {/* Selling Season */}
      <div css={filterControlCss}>
        <MultiSelectFilter
          resources={resources}
          label={getResource(resources, "ACCESS_OVERVIEW_SEASON")}
          clearLabel={getResource(resources, "ACCESS_OVERVIEW_CLEAR")}
          items={sellingSeasons}
          keyAccessor={{
            kind: "get",
            get: (p) => String(p.sellingSeasonId),
          }}
          labelAccessor={{
            kind: "field",
            field: "seasonDescription",
          }}
          selectedKeys={selectedSellingSeason}
          onChange={onSellingSeasonChange}
          showSearch={false}
          showClear
          controlWidth={200}
          panelMaxWidth={250}
          controlHeight={50}
        />
      </div>

      {/* Fulfillment Season */}
      <div css={filterControlCss}>
        <MultiSelectFilter
          resources={resources}
          label={getResource(resources, "ACCESS_OVERVIEW_FULFILLMENT_SEASON")}
          clearLabel={getResource(resources, "ACCESS_OVERVIEW_CLEAR")}
          items={fulfillmentSeasons}
          keyAccessor={{
            kind: "get",
            get: (p) => String(p.fulfillmentSeasonId),
          }}
          labelAccessor={{
            kind: "field",
            field: "seasonDescription",
          }}
          selectedKeys={selectedFulfillmentSeason}
          onChange={onFulfillmentSeasonChange}
          showSearch
          showClear
          controlWidth={275}
          panelMaxWidth={425}
          panelMaxHeight={500}
          controlHeight={50}
        />
      </div>

      {/* Location */}
      <div css={filterControlCss}>
        <MultiSelectFilter
          resources={resources}
          label={getResource(resources, "ACCESS_OVERVIEW_LOCATION")}
          clearLabel={getResource(resources, "ACCESS_OVERVIEW_CLEAR")}
          items={accessNodes}
          keyAccessor={{
            kind: "get",
            get: (p) => String(p.id),
          }}
          labelAccessor={{
            kind: "field",
            field: "name",
          }}
          selectedKeys={selectedAccessNodes}
          onChange={onAccessNodesChange}
          showSearch
          showClear
          controlWidth={250}
          panelMaxWidth={450}
          panelMaxHeight={600}
          controlHeight={50}
        />
      </div>

      {/* ID Filter */}
      <div css={filterControlCss}>
        <IdListSearchDropdown
          label={getResource(resources, "ACCESS_OVERVIEW_ID")}
          clearLabel={getResource(resources, "ACCESS_OVERVIEW_CLEAR")}
          value={entitlementTypeIdQuery}
          onChange={onIdQueryChange}
          onValidIdsChange={onValidIdListChange}
          searchPlaceholder={getResource(
            resources,
            "ACCESS_OVERVIEW_ID_PLACEHOLDER",
          )}
          onSearch={onSearch}
          controlWidth={150}
          controlHeight={50}
          panelMinWidth={350}
          panelMaxWidth={350}
          panelMaxHeight={125}
          rows={3}
          autoGrow
          iconSize={24}
        />
      </div>

      {/* Description Filter */}
      <div css={filterControlCss}>
        <TextSearchDropdown
          label={getResource(resources, "ACCESS_OVERVIEW_DESCRIPTION")}
          clearLabel={getResource(resources, "ACCESS_OVERVIEW_CLEAR")}
          value={entitlementTypeDescriptionQuery}
          onChange={onDescriptionQueryChange}
          searchPlaceholder={getResource(
            resources,
            "ACCESS_OVERVIEW_DESCRIPTION_PLACEHOLDER",
          )}
          maxChars={50}
          onSearch={onSearch}
          controlWidth={250}
          controlHeight={50}
          panelMaxWidth={350}
          panelMinWidth={350}
          rows={1}
          iconSize={24}
          closeOnEnter
        />
      </div>

      {/* SEARCH BUTTON */}
      <div css={filterControlCss}>
        <Buttons.MediumNeutral
          css={searchButtonCss}
          type="submit"
          id="search-button"
          title={getResource(resources, "ACCESS_OVERVIEW_SEARCH_TITLE")}
          onClick={onSearch}
        >
          {getResource(resources, "ACCESS_OVERVIEW_SEARCH")}
        </Buttons.MediumNeutral>
      </div>
    </Row>
  );
};
